'use client'

import { ReactNode, useRef } from 'react'
import { Button, Carousel, Cell, Divider, Image, Spacer, Text } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useAsset } from '@marketplace-web/shared/assets'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { HelpCenterFaqEntryUrl } from '@marketplace-web/help-center/help-center-faq-entry-url-feature'
import { FaqEntryType } from '@marketplace-web/help-center/help-center-faq-entry-url-data'

import ConversationExample from '../ConversationExample'
import { useSlideNavigation } from '../../../hooks/useSlideNavigation'
import { useManualMode } from '../../../hooks/useManualMode'
import { useEducationState } from '../../../hooks/useEducationState'
import {
  getDurationForExample,
  formatMessages,
  getButtonTranslationKey,
} from '../../../utils/slideHelpers'
import usePhishingFirstTimeListerEducationTracking from '../usePhishingFirstTimeListerEducationTracking'
import { IntroSlide } from './IntroSlide'
import { EducationSlide } from './EducationSlide'
import { SlideNavigation } from './SlideNavigation'
import { BulletPointList } from './BulletPointList'
import { AB_TEST_VARIANTS, type Conversation, type Slide } from './newContentTestConfig'

type SlidesProps = {
  setIsDialogOpen: (isOpen: boolean) => void
}

const Slides = ({ setIsDialogOpen }: SlidesProps) => {
  const educationContainerRef = useRef<HTMLDivElement>(null)

  const { isButtonEnabled, isEducationStarted, startEducation } = useEducationState()
  const {
    currentSlideIndex,
    currentConversationIndex,
    setCurrentSlideIndex,
    setCurrentConversationIndex,
  } = useSlideNavigation()
  const { isManualMode, enableManualMode, enableManualModeForAllSlides, setManualModeForSlide } =
    useManualMode()

  const phishingEducationAbTest = useAbTest('first_time_lister_education_ui_in_stories')
  const isNewVariant = phishingEducationAbTest?.variant === 'on'

  useTrackAbTest(phishingEducationAbTest)

  const config = AB_TEST_VARIANTS[isNewVariant ? 'new' : 'old']

  const translate = useTranslate('conversation.phishing_first_time_lister_education')
  const asset = useAsset('/assets/phishing-first-time-lister-education')

  const currentSlide = config.carouselSlides[currentSlideIndex]
  const hasReportSteps = currentSlide?.step1 || currentSlide?.step2 || currentSlide?.step3
  const maxConversationIndex = hasReportSteps ? 1 : (currentSlide?.conversations?.length || 1) - 1

  const { onSeeTipsClick, onSlideChange, onHelpCenterClick, onDialogClose } =
    usePhishingFirstTimeListerEducationTracking({
      educationContainerRef,
      isNewVariant,
      currentConversationIndex,
      maxConversationIndex,
      getExampleDuration: (slideIndex, exampleIndex) =>
        getDurationForExample(slideIndex, exampleIndex, config),
      currentSlideIndex,
    })

  const lastSlideIndex = config.carouselSlides.length - 1
  const isLastSlide = lastSlideIndex === currentSlideIndex

  const helpCentreTranslationValues = {
    'link-help-centre': (chunks: Array<ReactNode>) => (
      <HelpCenterFaqEntryUrl
        key="link-help-centre"
        type={FaqEntryType.SpamPhishing}
        render={faqUrl => (
          <a href={faqUrl} target="_blank" rel="noreferrer" onClick={onHelpCenterClick}>
            {chunks}
          </a>
        )}
      />
    ),
  }

  const handleSlideChange = (index: number, isBackwardNavigation: boolean = false) => {
    onSlideChange(currentSlideIndex, index)

    if (isBackwardNavigation) {
      enableManualModeForAllSlides(config.carouselSlides.length)
    } else {
      setManualModeForSlide(index, false)
    }

    setCurrentSlideIndex(index)
  }

  const handleButtonClick = () => {
    if (!isEducationStarted) {
      startEducation()
      onSeeTipsClick()

      return
    }

    if (isLastSlide) {
      setIsDialogOpen(false)
      onDialogClose()

      return
    }

    handleSlideChange(currentSlideIndex + 1, false)
  }

  const handleConversationNavigation = (direction: 'prev' | 'next') => {
    enableManualMode(currentSlideIndex)

    const slide = config.carouselSlides[currentSlideIndex]
    if (!slide) return

    let maxIndex = 0
    const slideHasReportSteps = slide.step1 || slide.step2 || slide.step3

    if (slideHasReportSteps) {
      maxIndex = 1
    } else if (slide.conversations?.length) {
      maxIndex = slide.conversations.length - 1
    } else {
      return
    }

    if (direction === 'next' && currentConversationIndex < maxIndex) {
      setCurrentConversationIndex(currentConversationIndex + 1)
    } else if (direction === 'prev' && currentConversationIndex > 0) {
      setCurrentConversationIndex(currentConversationIndex - 1)
    }
  }

  const handleConversationComplete = () => {
    const slide = config.carouselSlides[currentSlideIndex]
    if (!slide) return

    const maxIndex = slide.conversations?.length || 2
    if (currentConversationIndex < maxIndex - 1) {
      setCurrentConversationIndex(currentConversationIndex + 1)
    }
  }

  const renderConversationItemOldVariant = (
    { messages, avatarImageSrc }: Conversation,
    index: number,
  ) => {
    const formattedMessages = formatMessages(messages, translate, asset)
    const label = config.showConversationLabels
      ? translate('scam_message_example', { index: index + 1 })
      : ''

    return (
      <ConversationExample
        key={index}
        label={label}
        messages={formattedMessages}
        avatarImageSrc={asset(avatarImageSrc)}
        showSpacer={config.showConversationLabels}
        withBackground={config.showConversationBackground}
      />
    )
  }

  const renderSectionTitle = (sectionTitle?: string) => {
    if (!sectionTitle) return null

    return (
      <Text
        as="h3"
        text={translate(sectionTitle)}
        type={Text.Type.Title}
        alignment={Text.Alignment.Left}
        width={Text.Width.Parent}
      />
    )
  }

  const renderConversationsOldVariant = (
    conversations?: Array<Conversation>,
    sectionTitle?: string,
  ) => {
    if (!conversations?.length) return null

    const separatorSize = config.showConversationLabels ? Spacer.Size.X2Large : Spacer.Size.Large

    return (
      <>
        <Spacer size={Spacer.Size.X2Large} />
        {renderSectionTitle(sectionTitle)}
        {sectionTitle && <Spacer size={Spacer.Size.Regular} />}
        <SeparatedList separator={<Spacer size={separatorSize} />}>
          {conversations.map(renderConversationItemOldVariant)}
        </SeparatedList>
      </>
    )
  }

  const renderReportStepsOldVariant = (
    step1?: string,
    step2?: string,
    step3?: string,
    helpPrompt?: string,
    sectionTitle?: string,
  ) => {
    if (!step1 || !step2 || !step3) return null

    const steps = [step1, step2, step3]

    return (
      <>
        <Spacer size={Spacer.Size.X2Large} />
        {renderSectionTitle(sectionTitle)}
        <Spacer size={Spacer.Size.Regular} />
        <div className="phishing-first-time-lister-education__steps">
          <ol>
            {steps.map((text, index) => (
              <li key={index}>
                <Text
                  as="span"
                  text={translate(text)}
                  type={Text.Type.Subtitle}
                  theme="amplified"
                  inline
                />
              </li>
            ))}
          </ol>
        </div>
        {helpPrompt && (
          <>
            <Spacer size={Spacer.Size.Large} />
            <div className="u-force-wrap">{translate(helpPrompt, helpCentreTranslationValues)}</div>
          </>
        )}
      </>
    )
  }

  const renderSlideOldVariant = (slide: Slide, index: number) => (
    <Cell key={index}>
      {config.showIntroSpacer && <Spacer size={Spacer.Size.X2Large} />}
      <div className="u-flexbox">
        <Image
          src={asset(slide.image, { theme: { dark: `dark/${slide.image}` } })}
          size={Image.Size.X3Large}
          alt={translate(slide.title)}
          scaling={Image.Scaling.Contain}
        />
      </div>
      <Spacer size={Spacer.Size.Large} />
      <Text
        as="h1"
        text={translate(slide.title)}
        type={config.titleType}
        alignment={Text.Alignment.Center}
        width={Text.Width.Parent}
      />
      <Spacer size={Spacer.Size.Large} />
      <Text
        as="span"
        text={
          <div className="u-force-wrap">{translate(slide.body, helpCentreTranslationValues)}</div>
        }
        type={Text.Type.Body}
        html
      />
      {slide.bulletPoints && <BulletPointList bulletPoints={slide.bulletPoints} />}
      {renderConversationsOldVariant(slide.conversations, slide.sectionTitle)}
      {renderReportStepsOldVariant(
        slide.step1,
        slide.step2,
        slide.step3,
        slide.helpPrompt,
        slide.sectionTitle,
      )}
    </Cell>
  )

  const renderCarouselOldVariant = () => {
    if (!isEducationStarted) {
      return renderSlideOldVariant(config.introSlide, -1)
    }

    const slide = config.carouselSlides[currentSlideIndex]

    if (slide) {
      return renderSlideOldVariant(slide, currentSlideIndex)
    }

    return renderSlideOldVariant(config.introSlide, -1)
  }

  const renderCarouselNavigationOldVariant = () => {
    if (!isEducationStarted) return null

    return (
      <>
        <div className="u-flexbox">
          <Carousel.Navigation
            onBulletSelect={handleSlideChange}
            slidesCount={config.carouselSlides.length}
            activeSlide={currentSlideIndex}
            styling={Carousel.Styling.Floating}
          />
        </div>
        <Spacer size={Spacer.Size.Large} />
      </>
    )
  }

  const renderCarousel = () => {
    if (!isEducationStarted) {
      return <IntroSlide slide={config.introSlide} config={config} />
    }

    const slide = config.carouselSlides[currentSlideIndex]

    if (slide) {
      return (
        <EducationSlide
          slide={slide}
          slideIndex={currentSlideIndex}
          currentConversationIndex={currentConversationIndex}
          isEducationStarted={isEducationStarted}
          config={config}
          isManualMode={isManualMode}
          onConversationNavigate={handleConversationNavigation}
          onConversationComplete={handleConversationComplete}
          helpCentreTranslationValues={helpCentreTranslationValues}
        />
      )
    }

    return <IntroSlide slide={config.introSlide} config={config} />
  }

  const renderActions = () => {
    if (!isNewVariant) {
      return (
        <>
          {isEducationStarted && config.showDividerOnEducationOnly ? <Divider /> : null}
          <Cell>
            {renderCarouselNavigationOldVariant()}
            <Button
              text={translate(getButtonTranslationKey(isEducationStarted, isLastSlide, config))}
              styling={Button.Styling.Filled}
              onClick={handleButtonClick}
              disabled={!isButtonEnabled}
              testId="phishing-first-time-lister-education-action-button"
            />
          </Cell>
        </>
      )
    }

    return (
      <Cell>
        <Button
          text={translate(getButtonTranslationKey(isEducationStarted, isLastSlide, config))}
          styling={Button.Styling.Filled}
          onClick={handleButtonClick}
          disabled={!isButtonEnabled}
          testId="phishing-first-time-lister-education-action-button"
        />
      </Cell>
    )
  }

  return (
    <div className="u-fill-height u-flexbox u-flex-direction-column">
      {isNewVariant && isEducationStarted && (
        <SlideNavigation
          currentSlideIndex={currentSlideIndex}
          totalSlides={config.carouselSlides.length}
          onBackClick={() => handleSlideChange(currentSlideIndex - 1, true)}
        />
      )}
      <div
        key={isEducationStarted ? currentSlideIndex : 'intro'}
        className="phishing-first-time-lister-education__slide"
        ref={educationContainerRef}
      >
        {isNewVariant ? renderCarousel() : renderCarouselOldVariant()}
      </div>
      {renderActions()}
    </div>
  )
}

export default Slides
